import React, { useMemo } from "react";
import { barY, defineChart, ruleY, text } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";

import { ChartFrame } from "./ChartFrame";

export interface WaterfallItem {
    name: string;
    value: number;
    measure: "relative" | "total";
    text?: string;
}

interface WaterfallStep {
    name: string;
    start: number;
    end: number;
    kind: "increase" | "decrease" | "total";
    text: string;
}

const DEFAULT_COLORS = {
    increase: "#43a04c",
    decrease: "#bdbdbd",
    total: "#2186c5"
};

export function WaterfallChart({
    items,
    colors,
    yFormat,
    yLabel,
    ariaLabel,
    height = 320,
    width,
    style,
    className,
    onReady
}: {
    items: WaterfallItem[];
    colors?: { increase?: string; decrease?: string; total?: string };
    yFormat?: (value: number) => string;
    yLabel?: string;
    ariaLabel: string;
    height?: number;
    width?: number;
    style?: React.CSSProperties;
    className?: string;
    onReady?: () => void;
}): React.ReactElement {
    const paint = {
        increase: colors?.increase || DEFAULT_COLORS.increase,
        decrease: colors?.decrease || DEFAULT_COLORS.decrease,
        total: colors?.total || DEFAULT_COLORS.total
    };
    const definition = useMemo(() => {
        let running = 0;
        const steps: WaterfallStep[] = items.map((item) => {
            if (item.measure === "total") {
                const step: WaterfallStep = {
                    name: item.name,
                    start: 0,
                    end: item.value,
                    kind: "total",
                    text: item.text || ""
                };
                running = item.value;
                return step;
            }
            const start = running;
            running += item.value;
            return {
                name: item.name,
                start,
                end: running,
                kind: item.value >= 0 ? "increase" : "decrease",
                text: item.text || ""
            };
        });
        const names = steps.map((step) => step.name);
        return defineChart({
            marks: [
                decorative(ruleY([0], { stroke: "#e2e8f0" })),
                barY(steps, {
                    x: "name",
                    y1: "start",
                    y2: "end",
                    key: "name",
                    fill: (step) => paint[step.kind]
                }),
                decorative(
                    text(
                        steps.filter((step) => step.text),
                        {
                            x: "name",
                            y: (step) => (Math.abs(step.end) >= Math.abs(step.start) ? step.end : step.start),
                            text: "text",
                            dy: (step) => (step.end >= step.start ? -8 : 14),
                            anchor: "middle",
                            fill: "#334155",
                            fontSize: 11
                        }
                    )
                )
            ],
            scales: {
                x: {
                    scale: scaleBand<string>().domain(names).padding(0.35),
                    axis: { tickLabels: { rotate: names.length > 6 ? -30 : 0, fontSize: 10 } }
                },
                y: {
                    scale: scaleLinear,
                    nice: true,
                    grid: true,
                    axis: { label: yLabel, ticks: yFormat ? { format: yFormat } : undefined }
                }
            },
            tooltip: {
                use: tooltip,
                content: (points) => {
                    const point = points[0];
                    if (!point) return { rows: [] };
                    const step = point.datum as WaterfallStep | undefined;
                    const title = step?.name ?? point.groupLabel ?? String(point.xValue ?? "");
                    let valueText = step?.text ?? "";
                    if (!valueText && typeof point.yValue === "number" && Number.isFinite(point.yValue) && yFormat) {
                        valueText = `${yFormat(point.yValue)}%`;
                    }
                    return {
                        title,
                        color: point.color,
                        rows: valueText ? [{ label: "", value: valueText }] : []
                    };
                }
            }
        });
    }, [items, paint, yFormat, yLabel]);

    if (items.length === 0) return <div />;

    return (
        <ChartFrame
            definition={definition}
            ariaLabel={ariaLabel}
            ariaDescription={items.map((item) => item.name).join(", ")}
            height={height}
            width={width}
            style={style}
            className={className}
            onReady={onReady}
        />
    );
}
